import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, authHelpers, UserProfile } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  isFornecedor: boolean;
  isMentor: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUp: (email: string, password: string, fullName?: string, isSupplierRequested?: boolean) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFornecedor, setIsFornecedor] = useState(false);
  const [isMentor, setIsMentor] = useState(false);
  const [loading, setLoading] = useState(true);

  // Refs para manter os valores atuais de user e profile estáveis em callbacks assíncronos
  const currentUserRef = React.useRef<User | null>(null);
  const currentProfileRef = React.useRef<UserProfile | null>(null);

  // Sincronizar refs com o estado
  useEffect(() => {
    currentUserRef.current = user;
  }, [user]);

  useEffect(() => {
    currentProfileRef.current = profile;
  }, [profile]);

  // Contadores para debug
  const loadProfileCallCount = React.useRef(0);
  const updateAuthStateCallCount = React.useRef(0);
  const lastProfileLoadTime = React.useRef(0);

  // Carregar perfil do usuário
  const loadUserProfile = async (userId: string, userEmail?: string) => {
    try {
      // Debounce: evitar chamadas muito frequentes (mínimo 1 segundo entre chamadas)
      const now = Date.now();
      if (now - lastProfileLoadTime.current < 1000) {
        console.log('⏱️ Debouncing profile load request');
        return;
      }
      lastProfileLoadTime.current = now;
      
      loadProfileCallCount.current += 1;
      console.log('👤 Load profile #' + loadProfileCallCount.current, 'for user:', userId);
      
      // Usar email fornecido ou buscar do auth
      let email = userEmail;
      if (!email) {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('❌ User not found in auth:', userError);
          setProfile(null);
          setIsAdmin(false);
          setIsMentor(false);
          return;
        }
        
        if (!userData.user || !userData.user.email) {
          console.error('❌ No user data or email found');
          setProfile(null);
          setIsAdmin(false);
          setIsMentor(false);
          return;
        }
        
        email = userData.user.email;
      }
      
      // Verificar se já temos o perfil carregado para este usuário
      // Removido para permitir atualizações do perfil
      // if (profile && profile.email === email) {
      //   console.log('ℹ️ Profile already loaded for:', email);
      //   return;
      // }
      
      console.log('🔍 Searching for profile with email:', email);
      
      // Buscar perfil do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (profileError) {
        console.error('❌ Error loading profile:', profileError);
        setProfile(null);
        setIsAdmin(false);
        setIsFornecedor(false);
        setIsMentor(false);
        return;
      }

      if (profileData) {
        console.log('✅ Profile loaded:', profileData.full_name, '- Role:', profileData.role);
        setProfile(profileData);
        setIsAdmin(profileData.role === 'admin');
        setIsFornecedor(profileData.role === 'fornecedor');
        setIsMentor(profileData.role === 'mentor' || profileData.role === 'admin');
      } else {
        console.log('❌ No profile found for email:', email);
        setProfile(null);
        setIsAdmin(false);
        setIsFornecedor(false);
        setIsMentor(false);
      }
    } catch (error) {
      console.error('❌ Unexpected error loading profile:', error);
      setProfile(null);
      setIsAdmin(false);
      setIsFornecedor(false);
      setIsMentor(false);
    }
  };

  const updateAuthState = async (session: Session | null) => {
    updateAuthStateCallCount.current += 1;
    console.log('🔄 Update auth state #' + updateAuthStateCallCount.current, session?.user?.email || 'No session');
    
    if (session?.user) {
      const prevUser = currentUserRef.current;
      const isSameUser = prevUser && prevUser.id === session.user.id;
      
      setSession(prev => {
        if (!prev || prev.access_token !== session.access_token) {
          return session;
        }
        return prev;
      });
      
      setUser(prev => {
        if (!prev || prev.id !== session.user.id) {
          return session.user;
        }
        return prev;
      });
      
      // Se for o mesmo usuário e o perfil já estiver carregado, não recarregar
      if (isSameUser && currentProfileRef.current) {
        console.log('ℹ️ Same user and profile already loaded, skipping profile load');
      } else {
        await loadUserProfile(session.user.id, session.user.email);
      }
    } else {
      setSession(null);
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
      setIsFornecedor(false);
      setIsMentor(false);
    }
    
    setLoading(false);
  };

  // Inicializar autenticação
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🚀 Inicializando autenticação...');
        setLoading(true);
        
        // Limpar apenas dados de verificação pendente (não remover tokens de auth)
        const pendingVerificationKey = 'masterclub_pending_verification';
        if (localStorage.getItem(pendingVerificationKey)) {
          localStorage.removeItem(pendingVerificationKey);
        }
        
        // Obter sessão atual
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Erro ao obter sessão:', sessionError);
          setLoading(false);
          return;
        }
        
        if (session) {
          console.log('✅ Sessão encontrada:', session.user?.email);
        }
        
        await updateAuthState(session);

        // Escutar mudanças na autenticação
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('🔄 Auth event:', event, session?.user?.email || 'No user');
            
            // Limpar verificação pendente se login foi bem-sucedido
            if (event === 'SIGNED_IN' && session) {
              const pendingVerification = localStorage.getItem('masterclub_pending_verification');
              if (pendingVerification === 'true') {
                localStorage.removeItem('masterclub_pending_verification');
              }
            }
            
            // Tratar eventos específicos - evitar chamadas duplicadas
            switch (event) {
              case 'SIGNED_IN':
                // Aguardar um pouco para garantir que a sessão está estabilizada
                setTimeout(async () => {
                  await updateAuthState(session);
                }, 1000);
                break;
                
              case 'TOKEN_REFRESHED':
                // Para refresh de token, só atualizar se não temos perfil carregado
                if (!profile) {
                  await updateAuthState(session);
                } else {
                  console.log('ℹ️ Token refreshed but profile already loaded, skipping update');
                }
                break;
                
              case 'SIGNED_OUT':
                await updateAuthState(null);
                break;
                
              case 'USER_UPDATED':
                // Atualizar quando o usuário é modificado
                await updateAuthState(session);
                break;
                
              default:
                // Para outros eventos, não fazer nada para evitar chamadas desnecessárias
                console.log('ℹ️ Ignoring auth event:', event);
                break;
            }
          }
        );

        return () => {
          console.log('🛑 Desconectando listener de auth');
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('❌ Erro ao inicializar autenticação:', error);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Função de login
  const signIn = async (email: string, password: string) => {
    console.log('🔐 [SIGN_IN] Iniciando login para:', email);
    setLoading(true);
    
    try {
      const result = await authHelpers.signIn(email, password);
      
      if (!result.error && result.data.session) {
        console.log('✅ [SIGN_IN] Login realizado com sucesso!');
        // O onAuthStateChange vai lidar com o resto
        setTimeout(() => setLoading(false), 2000);
      } else {
        console.error('❌ [SIGN_IN] Erro no login:', result.error);
        setLoading(false);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ [SIGN_IN] Erro inesperado no login:', error);
      setLoading(false);
      return { data: null, error: { message: 'Erro inesperado no login' } };
    }
  };

  // Função de registro
  const signUp = async (email: string, password: string, fullName?: string, isSupplierRequested?: boolean) => {
    setLoading(true);
    const result = await authHelpers.signUp(email, password, fullName, isSupplierRequested);
    if (!result.error && result.data.session) {
      await updateAuthState(result.data.session);
    } else {
      setLoading(false);
    }
    return result;
  };



  // Função de logout
  const signOut = async () => {
    try {
      console.log('Realizando logout...');
      setLoading(true);
      await authHelpers.signOut();
      setProfile(null);
      setIsAdmin(false);
      setIsFornecedor(false);
      setSession(null);
      setUser(null);
      setLoading(false);
      console.log('Logout realizado com sucesso');
      

    } catch (error) {
      console.error('Erro durante logout:', error);
      // Mesmo com erro, limpar estado local
      setProfile(null);
      setIsAdmin(false);
      setIsFornecedor(false);
      setSession(null);
      setUser(null);
      setLoading(false);
      
      
    }
  };

  // Função para atualizar perfil
  const refreshProfile = async () => {
    if (user?.id && user?.email) {
      console.log('Refrescando perfil para usuário:', user.id);
      // Resetar o timestamp para forçar atualização
      lastProfileLoadTime.current = 0;
      await loadUserProfile(user.id, user.email);
    } else {
      console.log('Nenhum usuário para refrescar perfil');
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    isAdmin,
    isFornecedor,
    isMentor,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};